/*
 * Description:
 * ---------------------
 * The Home Wi-Fi screen's signal map: a walk someone made through the home
 * with the Weavian app, as the box read it (cloud-anchor/pkg/homewifi
 * survey.go). The signal is drawn only where the walk went; the router, the
 * places the person named and, where one would help, the spot for an
 * extender sit on it. A device is drawn on it only where the walk places it
 * within a few metres: one the box cannot place closely is left off rather
 * than drawn somewhere it is not. All wording lives in homeWifiWords.js.
 *
 * ! file components/HomeWifiMap.js
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import useVisibleInterval from "../hooks/useVisibleInterval";
import { nodeApiUrlFor } from "../serverApi";
import { fetchWithTimeout } from "../utils/fetchWithTimeout";
import * as W from "./homeWifiWords";

async function getJSON(url) {
  const res = await fetchWithTimeout(url).catch(() => null);
  return res?.ok ? res.json().catch(() => null) : null;
}

/** The box's walks and the one picked, read once and every 30 s. */
export function useWalks(selectedNode) {
  const url = useMemo(() => nodeApiUrlFor(selectedNode), [selectedNode]);
  const [list, setList] = useState(null);
  const [pick, setPick] = useState("");
  const [analysis, setAnalysis] = useState(null);
  const readList = useCallback(async () => {
    const body = await getJSON(url("/wifi/home/surveys"));
    setList(body?.surveys || []);
  }, [url]);
  useEffect(() => {
    readList();
  }, [readList]);
  useVisibleInterval(readList, 30000);
  const id = pick || list?.[0]?.id || "";
  useEffect(() => {
    if (!id) return undefined;
    let live = true;
    getJSON(url(`/wifi/home/surveys/${encodeURIComponent(id)}`)).then((a) => live && setAnalysis(a));
    return () => {
      live = false;
    };
  }, [url, id]);
  return { list, id, pick: setPick, walk: analysis && analysis.id === id ? analysis : null };
}

/* How close the walk must place a device for the map to draw it. */
const CLOSE_M = 4;

/* Where each device is on the shown walk's map: where a person put it,
   else where the walk places it within CLOSE_M; by hardware address. */
export function spotsOn(id, layout, places = {}) {
  const out = {};
  if (layout?.frame === "walk" && layout.walk === id) {
    for (const p of layout.devices || []) {
      if (p.by === "walk" && p.radius_m <= CLOSE_M) out[p.mac] = { x: p.x, y: p.y, radius: p.radius_m, by: "walk" };
    }
  }
  for (const [mac, p] of Object.entries(places)) {
    if (p.walk === id) out[mac] = { x: p.x, y: p.y, radius: 0, by: "person" };
  }
  return out;
}

export default function SignalMap({ walks, layout, places, devices = [], source = W.wifiSource(), canPlace, onPlace }) {
  const { list, id, pick, walk } = walks;
  const [placing, setPlacing] = useState("");
  if (list === null) return null;
  const spots = spotsOn(id, layout, places);
  const named = (mac) => {
    const d = devices.find((x) => x.mac === mac);
    return d ? W.displayName(d) : "A device";
  };
  return (
    <section className="hw-signal" aria-label="Signal">
      <div className="hw-sub-head">
        <h3>Signal</h3>
        {list.length > 1 && (
          <select className="dl-input hw-walk" value={id} onChange={(e) => pick(e.target.value)} aria-label="Which walk">
            {list.map((s) => <option key={s.id} value={s.id}>{walkLabel(s)}</option>)}
          </select>
        )}
        {list.length === 1 && <span className="hw-quiet">{walkLabel(list[0])}</span>}
      </div>
      {list.length === 0 && <p className="hw-quiet">{W.MAP_NONE}</p>}
      {walk && (
        <Map a={walk} spots={spots} named={named} source={source} placing={placing}
          onSpot={async (x, y) => { await onPlace(placing, { walk: id, x, y }); setPlacing(""); }} />
      )}
      {walk && canPlace && (
        <div className="hw-place">
          <select className="dl-input" value={placing} onChange={(e) => setPlacing(e.target.value)} aria-label="A device to put on the map">
            <option value="">{W.PLACE_PICK}</option>
            {devices.filter((d) => d.mac).map((d) => <option key={d.mac} value={d.mac}>{W.displayName(d)}</option>)}
          </select>
          {placing && <span className="hw-quiet">{W.placeHint(named(placing))}</span>}
          {placing && spots[placing]?.by === "person" && (
            <button type="button" className="dl-btn" onClick={async () => { await onPlace(placing, null); setPlacing(""); }}>{W.PLACE_OFF}</button>
          )}
          {placing && <button type="button" className="dl-btn" onClick={() => setPlacing("")}>Cancel</button>}
        </div>
      )}
    </section>
  );
}

function walkLabel(s) {
  const t = new Date(s.at);
  const when = Number.isNaN(t.getTime()) ? s.id : t.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  return `Walk of ${when}`;
}

export const FILL = { strong: "#16A34A", good: "#86EFAC", fair: "#FBBF24", weak: "#EF4444" };

function Map({ a, spots, named, source, placing, onSpot }) {
  const w = a.cols * a.cell_m;
  const h = a.rows * a.cell_m;
  // Metres to the drawing: y grows up in the walk, down on the screen.
  const sx = (x) => x - a.min_x;
  const sy = (y) => h - (y - a.min_y);
  const units = [...new Set((a.aps || []).map((ap) => ap.unit))];
  const unitAt = (u) => (a.aps || []).find((ap) => ap.unit === u);
  const path = (a.path || []).map((p) => `${sx(p.x).toFixed(2)},${sy(p.y).toFixed(2)}`).join(" ");
  // Labels keep about 13 px on the screen whatever the home's size or the
  // screen's. The drawing fits its box, so a metre is drawn at the smaller
  // of the box's two scales.
  const [ref, box] = useSize();
  const drawn = box.width && box.height ? Math.min(box.width / w, box.height / h) : 0;
  const u = drawn ? Math.max(Math.max(w, h) / 46, 13 / drawn) : Math.max(w, h) / 46;
  const label = { fontSize: u, letterSpacing: 0 };
  // A click on the drawing, in the walk's metres: the drawing fits its box,
  // centred, at the smaller of its two scales.
  const click = (e) => {
    if (!placing || !drawn) return;
    const r = e.currentTarget.getBoundingClientRect();
    const mx = (e.clientX - r.left - (r.width - w * drawn) / 2) / drawn;
    const my = (e.clientY - r.top - (r.height - h * drawn) / 2) / drawn;
    if (mx < 0 || my < 0 || mx > w || my > h) return;
    onSpot(Math.round((mx + a.min_x) * 10) / 10, Math.round((h - my + a.min_y) * 10) / 10);
  };
  // Every name on the drawing, where it would sit, kept apart below.
  const labels = [
    ...units.map((unit, i) => {
      const ap = unitAt(unit);
      return { key: `ap-${unit}`, x: sx(ap.x), y: sy(ap.y) - u * 1.3, text: units.length === 1 ? source.name : `${source.name} ${i + 1}` };
    }),
    ...(a.marks || []).filter((m) => m.kind !== "router").map((m, i) => ({ key: `mark-${i}`, x: sx(m.x), y: sy(m.y) - u * 1.1, text: m.label })),
    ...Object.entries(spots).map(([mac, p]) => ({ key: `dev-${mac}`, x: sx(p.x), y: sy(p.y) + u * 1.7, text: named(mac) })),
    ...(a.extender ? [{ key: "extender", x: sx(a.extender.x), y: sy(a.extender.y) + u * 2, text: "Extender here" }] : []),
  ];
  return (
    <div className="hw-map">
      <p className="hw-quiet">{W.mapHeadline(a)}</p>
      <svg ref={ref} viewBox={`0 0 ${w} ${h}`} className={`hw-map-svg${placing ? " hw-placing" : ""}`} role="img"
        aria-label="The home's Wi-Fi signal where the walk went" onClick={click}>
        {(a.cells || []).map((v, i) => {
          if (v == null) return null;
          const c = i % a.cols;
          const r = Math.floor(i / a.cols);
          return (
            <rect key={i} x={sx(a.min_x + c * a.cell_m) - a.cell_m / 2} y={sy(a.min_y + r * a.cell_m) - a.cell_m / 2}
              width={a.cell_m} height={a.cell_m} fill={FILL[W.signalTone(v)]} opacity="0.55" />
          );
        })}
        {path && <polyline points={path} fill="none" stroke="#334155" strokeWidth={u * 0.14} strokeDasharray={`${u * 0.5} ${u * 0.4}`} strokeLinejoin="round" opacity="0.45" />}
        {units.map((unit) => {
          const ap = unitAt(unit);
          return <circle key={unit} cx={sx(ap.x)} cy={sy(ap.y)} r={u * 0.75} fill="#0F172A" stroke="#fff" strokeWidth={u * 0.18} />;
        })}
        {(a.marks || []).filter((m) => m.kind !== "router").map((m, i) => (
          <rect key={i} x={sx(m.x) - u * 0.5} y={sy(m.y) - u * 0.5} width={u} height={u} rx={u * 0.2} fill="#fff" stroke="#0F172A" strokeWidth={u * 0.12} />
        ))}
        {Object.entries(spots).map(([mac, p]) => (
          <g key={mac}>
            {p.radius > 0 && <circle cx={sx(p.x)} cy={sy(p.y)} r={p.radius} fill="#2563EB" opacity="0.1" />}
            <circle cx={sx(p.x)} cy={sy(p.y)} r={u * 0.5} fill="#2563EB" stroke="#fff" strokeWidth={u * 0.15} />
          </g>
        ))}
        <Scale w={w} h={h} u={u} />
        {a.extender && <Star x={sx(a.extender.x)} y={sy(a.extender.y)} r={u * 0.95} />}
        {apart(labels, u, w).map((l) => (
          <text key={l.key} x={l.x} y={l.y} className="hw-map-label" style={label} textAnchor="middle">{l.text}</text>
        ))}
      </svg>
      <div className="hw-legend">
        {Object.keys(FILL).map((k) => (
          <span key={k}><i style={{ background: FILL[k] }} />{W.SIGNAL_WORDS[k]}</span>
        ))}
      </div>
      {(a.marks || []).filter((m) => m.kind !== "router").map((m, i) => <p key={i} className="hw-quiet">{W.markSentence(m)}</p>)}
      <details className="dl-measure">
        <summary>How the box knows</summary>
        {(a.aps || []).map((ap) => (
          <span key={ap.bssid}>
            {ap.band || "Network"} {ap.bssid}: placed from {ap.samples} readings{ap.anchored ? ", the walk having begun beside it" : ""};
            {" "}{Math.round(ap.a_dbm)} dBm a metre away, falling with an exponent of {ap.n}; readings stray {ap.fit_db} dB from that on average.
          </span>
        ))}
        <span>Each cell is {a.cell_m} m. The signal is drawn only within {a.reach_m} m of where the walk went.</span>
      </details>
    </div>
  );
}

/** Labels moved apart: each, in turn, moves a line down, then up, until it
    overlaps none placed before it, and stays inside the drawing w wide, u
    being the labels' size; a label may say its own half width. */
export function apart(labels, u, w) {
  const placed = [];
  const over = (a, b) => Math.abs(a.x - b.x) < (a.half + b.half) && Math.abs(a.y - b.y) < u * 1.05;
  for (const l of labels) {
    const half = Math.min(w / 2 - u, l.half ?? (l.text.length * u * 0.56) / 2 + u * 0.2);
    const x = Math.min(w - half, Math.max(half, l.x));
    const tries = [0, 1.2, -1.2, 2.4, -2.4, 3.6];
    const at = tries.map((t) => ({ ...l, x, y: l.y + t * u, half })).find((c) => !placed.some((p) => over(c, p)));
    placed.push(at || { ...l, x, half });
  }
  return placed;
}

/* A bar of a round number of metres, about a fifth of the drawing wide, in
   its lower left corner. */
function Scale({ w, h, u }) {
  const m = [1, 2, 5, 10, 20, 50].reverse().find((x) => x <= w / 5) || 1;
  return (
    <g transform={`translate(${u} ${h - u * 1.2})`}>
      <line x1={0} y1={0} x2={m} y2={0} stroke="#334155" strokeWidth={u * 0.18} />
      <text x={m + u * 0.5} y={u * 0.35} style={{ fontSize: u * 0.9, letterSpacing: 0, wordSpacing: 0 }} className="hw-map-label">{`${m} m`}</text>
    </g>
  );
}

/** A ref, and the size in pixels of the element it is put on: 0 by 0 until
    it is laid out. */
export function useSize() {
  const ref = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([e]) => setSize({ width: Math.round(e.contentRect.width), height: Math.round(e.contentRect.height) }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, size];
}

function Star({ x, y, r }) {
  const pts = [];
  for (let i = 0; i < 10; i++) {
    const rad = (Math.PI / 5) * i - Math.PI / 2;
    const rr = i % 2 === 0 ? r : r * 0.45;
    pts.push(`${(x + rr * Math.cos(rad)).toFixed(2)},${(y + rr * Math.sin(rad)).toFixed(2)}`);
  }
  return <polygon points={pts.join(" ")} fill="#4F46E5" stroke="#fff" strokeWidth={r * 0.12} />;
}
